import * as Sentry from "@sentry/react";
import React from "react";
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from "react-router-dom";
import { redactSensitiveUrl } from "utils/redactUrl";

import { config } from "./config";

/**
 * A message worth seeing in Sentry that is not an exception. A no-op where
 * Sentry was never initialized (local).
 */
export function reportMessage(
	message: string,
	extra?: Record<string, unknown>,
): void {
	Sentry.captureMessage(message, { level: "warning", extra });
}

export const sentry = {
	init: (dsn: string) => {
		Sentry.init({
			dsn,
			integrations: [
				Sentry.reactRouterV6BrowserTracingIntegration({
					useEffect: React.useEffect,
					useLocation,
					useNavigationType,
					createRoutesFromChildren,
					matchRoutes,
				}),
				Sentry.replayIntegration(),
			],
			tracesSampleRate: 1.0,
			// Replay was previously DISABLED despite replayIntegration() being
			// installed: the SDK defaults both replay sample rates to 0 and logs
			// "Replay is disabled because neither `replaysSessionSampleRate` nor
			// `replaysOnErrorSampleRate` are set." `tracesSampleRate` above governs
			// performance tracing only and does not enable Replay.
			//
			// Error-only: keep a replay when something actually broke, which is when
			// a bug report gets filed, without recording every ordinary session.
			// The privacy defaults are left untouched and are what make this safe on
			// accounting screens — replayIntegration() defaults maskAllText,
			// maskAllInputs and blockAllMedia to true, and captures no request or
			// response bodies (networkDetailAllowUrls defaults to []).
			//
			// Note this still yields NO replay for the worst case in this product —
			// a silently wrong number throws no error — so any consumer must treat
			// the replay id as optional.
			replaysSessionSampleRate: 0,
			replaysOnErrorSampleRate: 1.0,
			environment: config.env,
			// The static tag below is one copy of the URL. The SDK writes its OWN
			// copy on every event: `httpContextIntegration` is a DEFAULT integration
			// and sets `event.request.url` from `window.location.href`, raw. So the
			// redaction has to run on the event itself, not only at init.
			beforeSend: (event) => {
				if (event.request?.url) {
					event.request.url = redactSensitiveUrl(event.request.url);
				}
				return event;
			},
			beforeSendTransaction: (event) => {
				if (event.request?.url) {
					event.request.url = redactSensitiveUrl(event.request.url);
				}
				return event;
			},
			// Navigation breadcrumbs record path + query, so leaving the reset page
			// would otherwise re-capture the code after beforeSend cleaned the event.
			beforeBreadcrumb: (breadcrumb) => {
				if (breadcrumb.category === "navigation" && breadcrumb.data) {
					if (typeof breadcrumb.data.from === "string")
						breadcrumb.data.from = redactSensitiveUrl(breadcrumb.data.from);
					if (typeof breadcrumb.data.to === "string")
						breadcrumb.data.to = redactSensitiveUrl(breadcrumb.data.to);
				}
				return breadcrumb;
			},
			initialScope: {
				tags: {
					start: new Date().toISOString(),
					url: redactSensitiveUrl(window.location.href),
					host: window.location.hostname,
				},
			},
		});
	},
};
